import { MessageMedium } from 'constants/messageMediums';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useGraphql } from 'utils/hooks/useGraphql';
import { queryTemplateAndContentByIdForCloning } from '../../cloning.graphql';
import { useCloningCampaignsContext } from '../../context';
import { CampaignsTemplatesProps } from '../types';
import { getDefaultMessageTypeAndSender } from '../utils';
import { CampaignsTemplatesSelect } from './CampaignsTemplatesSelect';

const Template = ({ campaign }: CampaignsTemplatesProps) => {
  const { setValue } = useFormContext();
  const messageMedium = campaign?.messageMedium;
  const isMediumEmail = messageMedium === MessageMedium.Email;
  const isMediumSMS = messageMedium === MessageMedium.SMS;

  // cloning campaigns context data
  const { messageTypesAndSenders, areOptionsLoaded } = useCloningCampaignsContext();
  const { messageTypes = [], senders = [] } = messageTypesAndSenders[messageMedium] || {};

  // query to load to initial selected values from original campaign & template
  const { data, isLoading } = useGraphql(queryTemplateAndContentByIdForCloning, {
    variables: {
      id: campaign?.templateId,
    },
    fetchPolicy: 'no-cache',
  });

  // sets the default selection from og project if available in destination project
  useEffect(() => {
    if (isLoading) return;
    if (data) {
      const { templateAndContent } = data;
      const { messageType, sender } = getDefaultMessageTypeAndSender({
        isMediumEmail,
        isMediumSMS,
        messageTypes,
        senders,
        templateAndContent,
      });
      const messageTypeField = isMediumEmail || isMediumSMS ? messageMedium : '';

      setValue(`${campaign.id}.messageTypeId${messageTypeField}`, messageType, {
        shouldValidate: true,
      });

      if (isMediumEmail || isMediumSMS) {
        setValue(`${campaign.id}.sender${messageMedium}`, sender, {
          shouldValidate: true,
        });
      }
    }
  }, [data, messageTypes, senders]);

  return (
    <>
      {areOptionsLoaded && (
        <CampaignsTemplatesSelect
          campaignId={campaign?.id}
          campaignName={campaign?.name}
          senders={senders}
          messageTypes={messageTypes}
          optionsLoading={isLoading}
          messageMedium={messageMedium}
          isMediumSMS={isMediumSMS}
          isMediumEmail={isMediumEmail}
        />
      )}
    </>
  );
};

export const CampaignsTemplates = React.memo(Template);
